import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicTestimonials } from "@/hooks/useCMSTestimonials";
import { getLocalized } from "@/utils/localization";

export default function Testimonials() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const { testimonials, config } = usePublicTestimonials();
  const [active, setActive] = useState(0);

  if (!testimonials.length) return null;

  const safeActive = Math.min(active, testimonials.length - 1);
  const currentItem = testimonials[safeActive] as any;

  const sectionBadge = getLocalized(i18n.language, config.sectionBadge, (config as any).sectionBadgeEn) || t("testimonials.badge", "آراء المرضى");
  const sectionTitle = getLocalized(i18n.language, config.sectionTitle, (config as any).sectionTitleEn) || t("testimonials.heading", "ماذا يقول من عاشوا التجربة");
  const sectionSubtitle = getLocalized(i18n.language, config.sectionSubtitle, (config as any).sectionSubtitleEn) || t("testimonials.desc", "تجارب حقيقية لمرضى اختاروا الطب الدقيق كمسار لفهم صحتهم");

  const quoteText = getLocalized(i18n.language, currentItem.text, currentItem.textEn || currentItem.text_en);
  const authorName = getLocalized(i18n.language, currentItem.name, currentItem.nameEn || currentItem.name_en);
  const authorSpecialty = getLocalized(i18n.language, currentItem.specialty, currentItem.specialtyEn || currentItem.specialty_en);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="inline-block bg-brand-forest-100 text-brand-forest text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            {sectionBadge}
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900">{sectionTitle}</h2>
          <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>
        <div className="max-w-3xl mx-auto bg-brand-cream-50 rounded-3xl p-8 md:p-12 border border-brand-cream-200">
          {config.showRating && (
            <div className="flex gap-1 mb-6">
              {Array.from({ length: currentItem.rating || 5 }).map((_, s) => (
                <i key={s} className="ri-star-fill text-amber-400 text-lg"></i>
              ))}
              {Array.from({ length: 5 - (currentItem.rating || 5) }).map((_, s) => (
                <i key={s} className="ri-star-line text-amber-200 text-lg"></i>
              ))}
            </div>
          )}
          <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-8 font-medium">
            {isEn ? `“${quoteText}”` : `«${quoteText}»`}
          </p>
          <div className="flex items-center gap-4">
            {config.showImage && currentItem.image && (
              <img src={currentItem.image} alt={authorName} className="w-12 h-12 rounded-full object-cover object-top" />
            )}
            <div>
              <p className="font-bold text-gray-900 text-sm">{authorName}</p>
              <p className="text-brand-forest text-xs">{authorSpecialty}</p>
              {currentItem.verified && (
                <span className="inline-flex items-center gap-1 text-[10px] text-brand-forest-600 font-semibold mt-0.5">
                  <i className="ri-verified-badge-line text-xs"></i>
                  {isEn ? "Verified Patient" : "موثق"}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${i === safeActive ? "bg-brand-forest w-8" : "bg-brand-cream-300 w-2"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
